import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { DensityMode } from '../models/user-settings.model';

const DENSITY_STORAGE_KEY = 'progescode.density.active';

/** Base spacing scale copied from apps/styles/design-system/_spacing.scss (rem values). */
const BASE_SPACE_REM: Record<string, number> = {
  '0': 0,
  '1': 0.25,
  '2': 0.5,
  '3': 0.75,
  '4': 1,
  '5': 1.25,
  '6': 1.5,
  '8': 2,
  '10': 2.5,
  '12': 3,
  '16': 4,
  '20': 5,
  '24': 6,
};

/**
 * Both ranges anchor at the current baseline (multiplier 1.0) so a user who
 * never touches this setting sees zero visual change: Comfortable only ever
 * grows from baseline, Compact only ever shrinks from it.
 */
const RANGE_BY_MODE: Record<DensityMode, [number, number]> = {
  COMPACT: [0.7, 1.0],
  COMFORTABLE: [1.0, 1.4],
};

export const DEFAULT_DENSITY_MODE: DensityMode = 'COMFORTABLE';
export const DEFAULT_DENSITY_SCALE = 0;

/**
 * Runtime density engine — mirrors ThemeService.applyTheme()'s approach of
 * writing CSS custom properties on documentElement. Because Tailwind's
 * spacing utilities compile to `var(--space-N)` (not baked-in px values,
 * confirmed in apps/styles/tailwind/spacing.js), rewriting these vars
 * rescales padding/margin/gap across the whole app with no per-component work.
 */
@Injectable({ providedIn: 'root' })
export class DensityService {
  private readonly documentRef = inject(DOCUMENT);

  private readonly _mode = signal<DensityMode>(DEFAULT_DENSITY_MODE);
  private readonly _scale = signal<number>(DEFAULT_DENSITY_SCALE);

  readonly mode = this._mode.asReadonly();
  readonly scale = this._scale.asReadonly();

  apply(mode: DensityMode, scale: number, persistLocally = true): void {
    const clampedScale = Math.min(100, Math.max(0, scale));
    this._mode.set(mode);
    this._scale.set(clampedScale);

    const [min, max] = RANGE_BY_MODE[mode];
    const multiplier = min + ((max - min) * clampedScale) / 100;

    const root = this.documentRef.documentElement;
    for (const [key, remValue] of Object.entries(BASE_SPACE_REM)) {
      root.style.setProperty(`--space-${key}`, `${(remValue * multiplier).toFixed(3)}rem`);
    }
    root.setAttribute('data-density', mode.toLowerCase());

    if (persistLocally) {
      localStorage.setItem(DENSITY_STORAGE_KEY, JSON.stringify({ mode, scale: clampedScale }));
    }
  }

  /** Applies the localStorage cache (if any) for instant, pre-login paint; falls back to baseline. */
  initFromLocalStorage(): void {
    const raw = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { mode: DensityMode; scale: number };
        this.apply(parsed.mode, parsed.scale, false);
        return;
      } catch {
        // fall through to baseline
      }
    }
    this.apply(DEFAULT_DENSITY_MODE, DEFAULT_DENSITY_SCALE, false);
  }
}
